import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import OtpCodeInput from "./otp/OtpCodeInput";
import {
  startPasswordReset,
  verifyResetOtp,
  setNewPassword,
  otpError,
} from "../services/otpApi";
import "./Register.css";
import "./otp/Otp.css";

// =====================================================
// FORGOT PASSWORD — RESEND OTP   (route: /forgot-password)
//
// Replaces the Firebase password-reset email link entirely. There is no reset
// link any more, and no Firebase account to reset: MongoDB is the only password
// store, and this flow writes to it.
//
//     Enter email → Resend OTP → Verify OTP → New password → Confirm → Login
//
// ── WHY IT IS THREE SERVER CALLS AND NOT ONE ──
// Step 2 returns a single-use TICKET that step 3 must present. That is what
// makes "the reset page appears only after successful OTP verification" a fact
// rather than a piece of client-side routing: a user who skips to step 3 has no
// ticket, and the server refuses. The ticket is short-lived and is burned
// atomically, so it cannot be replayed to change the password twice.
//
// ── WHAT THIS SCREEN DELIBERATELY DOES NOT KNOW ──
// Whether the address has an account. The server answers step 1 identically
// either way, so this page always advances to the code step. A wrong address
// simply never receives a code — which is the correct outcome, and means this
// screen cannot be used to find out who has an account here.
// =====================================================

const STEP = { EMAIL: "email", CODE: "code", PASSWORD: "password", DONE: "done" };

function ForgotPassword() {
  const navigate = useNavigate();

  const [step, setStep] = useState(STEP.EMAIL);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [ticket, setTicket] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [invalid, setInvalid] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [devCode, setDevCode] = useState("");

  const submittingRef = useRef(false);

  /* =========================
     BACKGROUND — same field as Login / Register / Verify.
  ========================= */
  const canvasRef = useRef(null);
  const rootRef = useRef(null);
  const animRef = useRef(null);

  const makeParticle = useCallback((W, H) => {
    const COLORS = ["#6332ff", "#00ffc8", "#f472b6", "#60a5fa", "#a78bfa"];
    return {
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      r: Math.random() * 1.8 + 0.4,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      alpha: Math.random() * 0.6 + 0.2,
      pulse: Math.random() * Math.PI * 2,
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const root = rootRef.current;
    if (!canvas || !root) return;
    const ctx = canvas.getContext("2d");
    let W = root.offsetWidth;
    let H = root.offsetHeight;
    let particles = [];
    const resize = () => {
      W = root.offsetWidth;
      H = root.offsetHeight;
      canvas.width = W;
      canvas.height = H;
    };
    resize();
    particles = Array.from({ length: 60 }, () => makeParticle(W, H));
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.pulse += 0.018;
        if (p.x < 0) p.x = W;
        if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H;
        if (p.y > H) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha * (0.7 + 0.3 * Math.sin(p.pulse));
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    const ro = new ResizeObserver(resize);
    ro.observe(root);
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(animRef.current);
      ro.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, [makeParticle]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  /* =========================
     STEP 1 — SEND THE CODE
  ========================= */
  const sendCode = async (e) => {
    if (e) e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!value) {
      setError("Enter your email address.");
      return;
    }

    setBusy(true);
    setError("");
    setNotice("");

    try {
      const res = await startPasswordReset(value);
      setEmail(value);
      setDevCode(res.devCode || "");
      setNotice(res.message || "If that email is registered, we've sent it a code.");
      setCooldown(res.cooldownSeconds || 60);
      setCode("");
      setStep(STEP.CODE);
    } catch (err) {
      const e2 = otpError(err, "Could not send a code right now.");
      setError(e2.message);
      if (e2.retryAfterSeconds) setCooldown(Math.min(e2.retryAfterSeconds, 300));
      // A cooldown means a code WAS already sent, so move to the code step
      // anyway — the user has one in their inbox and is being told to use it.
      if (e2.reason === "COOLDOWN") {
        setEmail(value);
        setStep(STEP.CODE);
      }
    } finally {
      setBusy(false);
    }
  };

  /* =========================
     STEP 2 — VERIFY THE CODE → get the ticket
  ========================= */
  const verify = async (value) => {
    const entered = String(value || code).replace(/\D/g, "");
    if (entered.length < 4) {
      setError("Enter the code we emailed you.");
      return;
    }
    if (submittingRef.current) return;

    submittingRef.current = true;
    setBusy(true);
    setError("");
    setInvalid(false);

    try {
      const res = await verifyResetOtp(email, entered);
      setTicket(res.ticket);
      setNotice("Code verified — choose a new password.");
      setStep(STEP.PASSWORD);
    } catch (err) {
      const e = otpError(err, "That code could not be verified.");
      setError(e.message);
      setInvalid(true);
      setCode("");
      setTimeout(() => setInvalid(false), 400);
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  /* =========================
     STEP 3 — SET THE NEW PASSWORD
     Both fields are checked here AND on the server. The client check is there
     to catch a typo before a single-use ticket is spent on it; the server check
     is there because a browser check is not a control.
  ========================= */
  const savePassword = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("The two passwords do not match.");
      return;
    }

    setBusy(true);
    setError("");

    try {
      await setNewPassword({
        email,
        ticket,
        newPassword: password,
        confirmPassword: confirm,
      });
      setStep(STEP.DONE);
    } catch (err) {
      const e2 = otpError(err, "Could not update your password.");
      setError(e2.message);
      // The ticket is single use: if the server rejected it (expired, already
      // spent), sending the user back to the code step is the only way
      // forward — the password form would fail identically every time.
      if (e2.reason === "INVALID_TICKET") {
        setTicket("");
        setCode("");
        setStep(STEP.CODE);
      }
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0 || busy) return;
    setCode("");
    await sendCode();
  };

  const stepChip = (name, key) => {
    const order = [STEP.EMAIL, STEP.CODE, STEP.PASSWORD];
    const at = order.indexOf(step);
    const mine = order.indexOf(key);
    const cls = mine < at ? "otp-step otp-step-done" : mine === at ? "otp-step otp-step-active" : "otp-step";
    return <span className={cls}>{name}</span>;
  };

  return (
    <div className="rg-root" ref={rootRef}>
      <canvas className="rg-canvas" ref={canvasRef} />
      <div className="rg-aurora" />

      <div className="rg-card">
        <div className="rg-card-scanline" />
        <div className="rg-brand-label">✦ TheBlogsSphere</div>

        {step !== STEP.DONE && (
          <div className="otp-steps">
            {stepChip("Email", STEP.EMAIL)}
            <span className="otp-step-sep">→</span>
            {stepChip("Code", STEP.CODE)}
            <span className="otp-step-sep">→</span>
            {stepChip("New password", STEP.PASSWORD)}
          </div>
        )}

        {/* ── STEP 1 ── */}
        {step === STEP.EMAIL && (
          <>
            <h2 className="rg-title">Reset your password 🔑</h2>
            <p className="rg-subtitle">we'll email you a 6-digit code</p>

            <form className="rg-form" onSubmit={sendCode}>
              <div className="rg-field-group">
                <label className="rg-label" htmlFor="fp-email">Email</label>
                <input
                  id="fp-email"
                  className="rg-input"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError("");
                  }}
                  autoFocus
                  required
                />
              </div>

              {error && <p className="otp-error">{error}</p>}

              <button className="rg-submit" disabled={busy}>
                {busy ? (
                  <span className="rg-spinner-row">
                    <span className="rg-spinner" />
                    Sending…
                  </span>
                ) : (
                  "Send code →"
                )}
              </button>
            </form>
          </>
        )}

        {/* ── STEP 2 ── */}
        {step === STEP.CODE && (
          <>
            <h2 className="rg-title">Enter your code 🔐</h2>
            <p className="otp-sent-to">
              If <strong>{email}</strong> has an account, a 6-digit code is on its way.
            </p>

            <form
              className="rg-form"
              onSubmit={(e) => {
                e.preventDefault();
                verify();
              }}
            >
              <OtpCodeInput
                value={code}
                onChange={(v) => {
                  setCode(v);
                  if (error) setError("");
                }}
                disabled={busy}
                invalid={invalid}
                onComplete={(v) => verify(v)}
                label="Password reset code"
              />

              {error && <p className="otp-error">{error}</p>}
              {!error && notice && <p className="otp-ok">{notice}</p>}

              <button className="rg-submit" style={{ marginTop: 18 }} disabled={busy}>
                {busy ? (
                  <span className="rg-spinner-row">
                    <span className="rg-spinner" />
                    Verifying…
                  </span>
                ) : (
                  "Verify code →"
                )}
              </button>
            </form>

            <div className="otp-resend-row">
              <button
                type="button"
                className="otp-resend-btn"
                onClick={resend}
                disabled={cooldown > 0 || busy}
              >
                {cooldown > 0 ? `Resend code in ${cooldown}s` : "Didn't get it? Resend code"}
              </button>
              <span className="otp-hint">The code expires in 10 minutes.</span>
            </div>

            <div className="otp-spam-note">
              ⚠️ <strong>No email?</strong> Check your <b>Promotions / Spam / Junk / Social</b> folder.
            </div>

            {devCode && (
              <div className="otp-dev-banner">
                DEV MODE — no mail provider configured.<br />
                Your code: <strong>{devCode}</strong>
              </div>
            )}

            <button
              type="button"
              className="rg-link-btn"
              onClick={() => {
                setStep(STEP.EMAIL);
                setCode("");
                setError("");
                setNotice("");
              }}
            >
              Use a different email
            </button>
          </>
        )}

        {/* ── STEP 3 ── */}
        {step === STEP.PASSWORD && (
          <>
            <h2 className="rg-title">Choose a new password 🔒</h2>
            <p className="rg-subtitle">for {email}</p>

            <form className="rg-form" onSubmit={savePassword}>
              <div className="rg-field-group">
                <label className="rg-label" htmlFor="fp-new">New password</label>
                <div className="rg-password-wrap">
                  <input
                    id="fp-new"
                    className="rg-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="Min. 6 characters"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (error) setError("");
                    }}
                    autoComplete="new-password"
                    autoFocus
                    required
                  />
                  <span className="rg-eye" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? "🙈" : "👁"}
                  </span>
                </div>
              </div>

              <div className="rg-field-group">
                <label className="rg-label" htmlFor="fp-confirm">Confirm password</label>
                <input
                  id="fp-confirm"
                  className="rg-input"
                  type={showPassword ? "text" : "password"}
                  placeholder="Type it again"
                  value={confirm}
                  onChange={(e) => {
                    setConfirm(e.target.value);
                    if (error) setError("");
                  }}
                  autoComplete="new-password"
                  required
                />
              </div>

              {error && <p className="otp-error">{error}</p>}

              <div className="rg-reminder">
                🔑 Note : Remember this password. You need it every time to login.
              </div>

              <button className="rg-submit" disabled={busy}>
                {busy ? (
                  <span className="rg-spinner-row">
                    <span className="rg-spinner" />
                    Saving…
                  </span>
                ) : (
                  "Save new password →"
                )}
              </button>
            </form>
          </>
        )}

        {/* ── DONE ── */}
        {step === STEP.DONE && (
          <>
            <h2 className="rg-title">Password updated ✅</h2>
            <p className="rg-subtitle">you can log in with your new password</p>
            <button
              className="rg-submit"
              style={{ marginTop: 18 }}
              onClick={() => navigate("/", { replace: true })}
            >
              Go to login →
            </button>
          </>
        )}

        <div className="rg-divider" />

        <p className="rg-footer-text">
          Remembered it? <Link to="/" className="rg-link">Back to login</Link>
        </p>
      </div>
    </div>
  );
}

export default ForgotPassword;
